<template>
  <div
    :class="{
      'global-table-wrapper': true,
      'filled': filled,
      'no-footer': !hasFooter
    }"
    ref="globalTableWrapper"
  >
    <workbench-table
      ref="table"
      v-bind="$props"
      :columns="localColumns"
      :data="localDataSource"
      :height="localTableHeight"
      :loading="localLoading"
      placeholder="-"
      @on-row-click="onRowClick"
      @on-row-dblclick="onRowDbclick"
      @on-expand="onExpand"
      @on-sort-change="onSortChange"
      @on-selection-change="onSelectionChange"
    >
      <template
        v-for="(item, key) in $scopedSlots"
        v-slot:[key]="{ row, column, index }"
      >
        <slot
          :name="key"
          v-bind="{row, column, index}"
        ></slot>
      </template>
    </workbench-table>
    <a-row
      v-if="$scopedSlots.footerLeft || showPagination !== false"
      type="flex"
      justify="end"
      class="global-table-wrapper__footer"
    >
      <div class="global-table-wrapper__footer--left">
        <slot name="paginationLeft"></slot>
      </div>
      <a-pagination
        size="small"
        v-bind="localPagination"
        @change="onPaginationChange"
        @showSizeChange="onPaginationChange"
      ></a-pagination>
    </a-row>
  </div>
</template>

<script>
import elementResizeDetectorMaker from 'element-resize-detector';
import WorkbenchTable from '@3vjia/workbench-table/es/workbench-table';
import '@3vjia/workbench-table/es/workbench-table/style';

export default {
  name: 'GlobalTable',
  components: {
    WorkbenchTable,
  },
  props: Object.assign({}, WorkbenchTable.props, {
    isShowTotal: {
      type: [String, Boolean],
      default: true,
    },
    dataRender: {
      type: [Function, Array],
      required: true,
    },
    pageNum: {
      type: Number,
      default: 1,
    },
    pageSize: {
      type: Number,
      default: 10,
    },
    showPagination: {
      type: [String, Boolean],
      default: true,
    },
    /**
     * 是否启动url带分页参数
     */
    pageURI: {
      type: Boolean,
      default: false,
    },
    filled: {
      type: Boolean,
      default: false,
    },
    pagination: {
      type: Object,
      default() {
        return {};
      },
    },
  }),
  data() {
    if (this.isShowTotal) {
      return {
        localTotalNum: 0,
        localLoading: false,
        localTableHeight: null,
        localScroll: this.scroll,
        localBodyStyle: null,
        localDataSource: [],
        localPagination: Object.assign(
          {
            showQuickJumper: true,
            showSizeChanger: true,
            showTotal(total) {
              return `共 ${total} 条`;
            },
          },
          this.pagination,
        ),
      };
    }
    return {
      localTotalNum: 0,
      localLoading: false,
      localTableHeight: null,
      localScroll: this.scroll,
      localBodyStyle: null,
      localDataSource: [],
      localPagination: Object.assign(
        {
          showQuickJumper: true,
          showSizeChanger: true,
          showTotal() {
            return '';
          },
        },
        this.pagination,
      ),
    };
  },
  computed: {
    hasFooter() {
      return this.$scopedSlots.footerLeft || this.showPagination !== false;
    },
    localColumns() {
      // 首末列增加class
      const columns = this.columns || [];
      let firstIndex = null;
      let lastIndex = null;
      if (columns.length) {
        lastIndex = columns.length - 1;
        for (let index = 0; index < columns.length; index += 1) {
          const element = columns[index];
          if (firstIndex === null && element.fixed === 'left') {
            firstIndex = index;
          }
          if (element.fixed === 'right') {
            lastIndex = index;
          }
        }
        firstIndex = firstIndex || 0;
        const firstColumn = columns[firstIndex];
        const lastColumn = columns[lastIndex];
        firstColumn.className = [firstColumn.className, 'first-column']
          .filter(item => item)
          .join(' ');
        lastColumn.className = [lastColumn.className, 'last-column']
          .filter(item => item)
          .join(' ');
      }
      return columns;
    },
  },
  watch: {
    'localPagination.current': function watchCurrent(val) {
      if (this.pageURI) {
        this.$router.push({
          ...this.$route,
          name: this.$route.name,
          params: Object.assign({}, this.$route.params, {
            pageNum: val,
          }),
        });
      }
    },
    pageNum(val) {
      Object.assign(this.localPagination, {
        current: val,
      });
    },
    pageSize(val) {
      Object.assign(this.localPagination, {
        pageSize: val,
      });
    },
    dataRender() {
      this.loadData();
    },
    localTotalNum() {
      this.$emit('handleTotalChange', this.localTotalNum);
    },
  },
  created() {
    const { pageNum } = this.$route.params;
    const localPageNum = (this.pageURI && (pageNum && parseInt(pageNum, 10))) || this.pageNum;
    if (['auto', true].includes(this.showPagination)) {
      this.localPagination = Object.assign({}, this.localPagination, {
        current: localPageNum,
        pageSize: this.pageSize,
      });
    }
    // 处理阻塞二级菜单展开
    setTimeout(() => {
      this.loadData();
    }, 300);
  },
  mounted() {
    if (this.filled) {
      this.observerTableWrapper = elementResizeDetectorMaker();
      this.observerTableWrapper.listenTo(this.$refs.globalTableWrapper, this.resize);
    }
  },
  beforeDestroy() {
    if (this.filled) {
      this.observerTableWrapper.removeListener(this.$refs.globalTableWrapper, this.resize);
    }
  },
  methods: {
    /**
     * 表格重新加载方法
     * 如果参数为 true, 则强制刷新到第一页
     * @param Boolean bool
     */
    refresh(bool = false) {
      if (bool) {
        this.localDataSource = [];
        this.localPagination = Object.assign({}, this.localPagination, {
          current: 1,
          pageSize: this.pageSize,
        });
      }
      this.loadData();
    },
    /**
     * 加载数据方法
     * @param {Object} pagination 分页选项器
     * @param {Object} filters 过滤条件
     * @param {Object} sorter 排序条件
     */
    loadData(pagination, filters, sorter) {
      if (typeof this.dataRender === 'function') {
        this.localLoading = true;
        const parameter = Object.assign(
          {
            pageIndex:
              (pagination && pagination.current) || this.localPagination.current || this.pageNum,
            pageSize:
              (pagination && pagination.pageSize) || this.localPagination.pageSize || this.pageSize,
          },
          {
            ...filters,
          },
          (sorter
            && sorter.key && {
            orders: [
              {
                column: sorter.key,
                type: sorter.order === 'asc' ? 0 : 1,
              },
            ],
          })
            || {},
        );
        const result = this.dataRender(parameter);
        // 对接自己的通用数据接口需要修改下方代码中的 res.pageNum, res.total, res.list
        if (
          (typeof result === 'object' || typeof result === 'function')
          && typeof result.then === 'function'
        ) {
          result
            .then((res) => {
              if (res.success) {
                const bizResult = res.result;
                this.localPagination = Object.assign({}, this.localPagination, {
                  current: bizResult.pageIndex || 1, // 返回结果中的当前分页数
                  total: bizResult.recordCount, // 返回结果中的总记录数
                  pageSize: (pagination && pagination.pageSize) || this.localPagination.pageSize,
                });
                this.localTotalNum = bizResult.recordCount;
                // 为防止删除数据后导致页面当前页面数据长度为 0 ,自动翻页到上一页
                if (bizResult.result && bizResult.result.length === 0 && this.localPagination.current > 1) {
                  this.localPagination.current -= 1;
                  this.loadData();
                  return;
                }
                // 这里用于判断接口是否有返回 res.totalCount 或 this.showPagination = false
                // 当情况满足时，表示数据不满足分页大小，关闭 table 分页功能
                if (!this.showPagination || (!bizResult.recordCount && this.showPagination === 'auto')) {
                  this.localPagination.hideOnSinglePage = true;
                }
                this.localDataSource = bizResult.result || []; // 返回结果中的数组数据
                this.localLoading = false;
              } else {
                this.localDataSource = []; // 返回结果中的数组数据
                this.localLoading = false;
              }
            })
            .catch(() => {
              this.localLoading = false;
            });
        }
      } else {
        this.localDataSource = this.dataRender || [];
      }
    },

    /**
     * 重置组件样式结构
     */
    resize() {
      if (this.filled) {
        this.$nextTick(() => {
          if (this.$refs.globalTableWrapper) {
            let footerHeight = 0;
            if (this.hasFooter) {
              footerHeight = 64;
            }
            this.localTableHeight = this.$refs.globalTableWrapper.offsetHeight - footerHeight;
          }
        });
      } else {
        this.localTableHeight = null;
      }
    },
    onRowClick(...args) {
      this.$emit('rowClick', ...args);
    },
    onRowDbclick(...args) {
      this.$emit('rowDbclick', ...args);
    },
    onExpand(...args) {
      this.$emit('expandChange', ...args);
    },
    onSortChange(sorter) {
      this.loadData(null, {}, sorter);
      this.$emit('sortChange', sorter);
    },
    onSelectionChange(selection) {
      this.$emit('selectionChange', selection);
    },
    exportCsv(...args) {
      this.$refs.table.exportCsv(...args);
    },
    clearCurrentRow(...args) {
      this.$refs.table.clearCurrentRow(...args);
    },
    selectAll(status) {
      this.$refs.table.selectAll(status);
    },
    onPaginationChange(current, pageSize) {
      this.loadData({ current, pageSize });
      this.$emit('paginationChange', current, pageSize);
    },
  },
};
</script>

<style lang="less">
@import url('./index.less');
</style>
